import React, { useEffect, useState } from 'react';
import { Container, ListGroup, Badge, Spinner, Alert, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { apiClient } from '@/services/apiClient';
import './MyReviews.scss';

interface MyReviewDto {
  guid: string;
  reviewName: string;
  templateName: string;
  category: string;
  reviewStatus: string;
  assignedDate: string;
  completedDate?: string;
  isComplete: boolean;
  totalTasks: number;
  completedTasks: number;
  progressPercent: number;
}

const MyReviews: React.FC = () => {
  const [reviews, setReviews] = useState<MyReviewDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadReviews();
  }, []);

  // Auto-refresh when user returns to page
  useEffect(() => {
    const handleFocus = () => {
      loadReviews();
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  const loadReviews = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get<MyReviewDto[]>('/api/reviews/my');
      setReviews(response.data);
    } catch (err: any) {
      setError(err.message || 'Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" />
        <p className="mt-2">Loading reviews...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  const isCancelled = (status?: string) => (status || '').toLowerCase() === 'cancelled';
  const activeReviews = reviews.filter(r => !r.isComplete && !isCancelled(r.reviewStatus));
  const cancelledReviews = reviews.filter(r => isCancelled(r.reviewStatus));
  const completedReviews = reviews.filter(r => r.isComplete && !isCancelled(r.reviewStatus));

  return (
    <Container className="mt-4">
      <h2>My Reviews</h2>
      <p className="text-muted">
        Track assigned procedures, checklists, and testing tasks
      </p>

      <div className="mb-4">
        <Link to="/review-sets">
          <Button variant="outline-primary" size="sm">
            View My Review Sets
          </Button>
        </Link>
      </div>

      {activeReviews.length === 0 && completedReviews.length === 0 && cancelledReviews.length === 0 && (
        <Alert variant="info">
          No reviews assigned yet.
        </Alert>
      )}

      {activeReviews.length > 0 && (
        <>
          <h4 className="mt-4 mb-3">Active Reviews</h4>
          <ListGroup className="mb-4">
            {activeReviews.map(review => (
              <ListGroup.Item
                key={review.guid}
                as={Link}
                to={`/reviews/${review.guid}`}
                action
                className="d-flex justify-content-between align-items-start review-item"
                style={{ transition: 'all 0.2s ease', borderRadius: '8px', marginBottom: '8px' }}
              >
                <div className="flex-grow-1">
                  <div className="fw-bold">{review.reviewName}</div>
                  <div className="text-muted small">
                    Template: {review.templateName} • Category: {review.category}
                  </div>
                  <div className="small text-muted">
                    Assigned: {new Date(review.assignedDate).toLocaleDateString()}
                  </div>
                </div>
                <div className="text-end ms-3">
                  <Badge bg="primary" className="mb-1">
                    {review.completedTasks} / {review.totalTasks} tasks
                  </Badge>
                  <div className="small text-muted">
                    {review.progressPercent}% complete
                  </div>
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </>
      )}

      {completedReviews.length > 0 && (
        <>
          <h4 className="mt-4 mb-3">Completed Reviews</h4>
          <ListGroup>
            {completedReviews.map(review => (
              <ListGroup.Item
                key={review.guid}
                as={Link}
                to={`/reviews/${review.guid}`}
                action
                className="d-flex justify-content-between align-items-start review-item"
                style={{ transition: 'all 0.2s ease', borderRadius: '8px', marginBottom: '8px' }}
              >
                <div className="flex-grow-1">
                  <div className="fw-bold">{review.reviewName}</div>
                  <div className="text-muted small">
                    Template: {review.templateName} • Category: {review.category}
                  </div>
                  <div className="small text-muted">
                    Completed: {review.completedDate ? new Date(review.completedDate).toLocaleDateString() : 'N/A'}
                  </div>
                </div>
                <Badge bg="success">Complete</Badge>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </>
      )}

      {cancelledReviews.length > 0 && (
        <>
          <h4 className="mt-4 mb-3">Cancelled Reviews</h4>
          <ListGroup>
            {cancelledReviews.map(review => (
              <ListGroup.Item
                key={review.guid}
                as={Link}
                to={`/reviews/${review.guid}`}
                action
                className="d-flex justify-content-between align-items-start review-item"
                style={{ transition: 'all 0.2s ease', borderRadius: '8px', marginBottom: '8px' }}
              >
                <div className="flex-grow-1">
                  <div className="fw-bold">{review.reviewName}</div>
                  <div className="text-muted small">
                    Template: {review.templateName} • Category: {review.category}
                  </div>
                  <div className="small text-muted">
                    Cancelled: {review.completedDate ? new Date(review.completedDate).toLocaleDateString() : 'N/A'}
                  </div>
                </div>
                <Badge bg="danger">Cancelled</Badge>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </>
      )}
    </Container>
  );
};

export default MyReviews;
